import { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, NativeSelect } from '@/components/ui/Input';
import { Checkbox } from '@/components/ui/Checkbox';
import { FormField } from '@/components/ui/FormField';
import { Badge } from '@/components/ui/Badge';
import { LoadingState, ErrorState } from '@/components/ui/States';
import { ApiError } from '@/lib/api/client';
import VoiceMicButton from '@/components/VoiceMicButton';
import VoiceFormFiller, { type VoiceField } from '@/components/VoiceFormFiller';
import { useVisitDiagnoses, useCreateDiagnosis, useRetractDiagnosis } from './useEncounterDiagnoses';
import type { EncounterDiagnosis, DiagnosisVerificationStatus } from './types';

const VERIFICATION_OPTIONS: DiagnosisVerificationStatus[] = ['provisional', 'differential', 'confirmed'];

const VOICE_FIELDS: VoiceField[] = [
  { key: 'diagnosis', label: 'Diagnosis', type: 'text' },
  { key: 'verification_status', label: 'Certainty', type: 'text', options: VERIFICATION_OPTIONS },
];

interface DiagnosisPanelProps {
  visitCode: string;
  uhid?: string;
}

export function DiagnosisPanel({ visitCode, uhid }: DiagnosisPanelProps) {
  const [adding, setAdding] = useState(false);
  const [diagnosis, setDiagnosis] = useState('');
  const [primary, setPrimary] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState<DiagnosisVerificationStatus>('provisional');
  const [formError, setFormError] = useState('');

  const query = useVisitDiagnoses(visitCode);
  const createMutation = useCreateDiagnosis(visitCode, uhid);
  const retractMutation = useRetractDiagnosis(visitCode, uhid);

  const resetForm = () => {
    setAdding(false); setDiagnosis(''); setPrimary(false); setVerificationStatus('provisional'); setFormError('');
  };

  const applyVoiceFields = (extracted: Record<string, string>) => {
    if (extracted.diagnosis) setDiagnosis(extracted.diagnosis);
    if (extracted.verification_status && (VERIFICATION_OPTIONS as string[]).includes(extracted.verification_status)) {
      setVerificationStatus(extracted.verification_status as DiagnosisVerificationStatus);
    }
  };

  const submit = async () => {
    const trimmed = diagnosis.trim();
    if (!trimmed) { setFormError('A diagnosis is required'); return; }
    setFormError('');
    try {
      await createMutation.mutateAsync({
        diagnosis: trimmed,
        rank: primary ? 1 : null,
        verification_status: verificationStatus,
      });
      resetForm();
    } catch (e) {
      // The backend's own message already explains the fix ("demote the
      // existing primary first") for the one-primary-per-visit conflict —
      // shown as-is rather than replaced with a generic line.
      setFormError(e instanceof Error ? e.message : String(e));
    }
  };

  const diagnoses = query.data ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>🩺 Diagnoses (this visit)</CardTitle>
        {!adding && <Button size="sm" variant="ghost" onClick={() => setAdding(true)}>+ Add diagnosis</Button>}
      </CardHeader>
      <CardContent>
        {query.isLoading && <LoadingState label="Loading diagnoses…" />}
        {query.isError && (
          <ErrorState
            message={query.error instanceof ApiError ? query.error.message : 'Could not load diagnoses'}
            onRetry={() => query.refetch()}
          />
        )}

        {!query.isLoading && !query.isError && diagnoses.length === 0 && !adding && (
          <div className="text-[13px] text-ink-muted">No diagnosis recorded for this visit yet.</div>
        )}

        {!query.isLoading && diagnoses.length > 0 && (
          <div className="flex flex-col gap-2">
            {diagnoses.map((d: EncounterDiagnosis) => (
              <div key={d.id} className="flex items-start justify-between gap-2 rounded-[var(--radius)] border border-border px-3 py-2">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-[13px] font-semibold">{d.diagnosis}</span>
                    {d.rank === 1 && <Badge variant="info">Primary</Badge>}
                    <Badge variant={d.verification_status === 'confirmed' ? 'success' : 'neutral'}>{d.verification_status}</Badge>
                  </div>
                </div>
                <Button size="sm" variant="ghost" loading={retractMutation.isPending} onClick={() => retractMutation.mutate(d.id)}>✕</Button>
              </div>
            ))}
          </div>
        )}

        {retractMutation.isError && (
          <div className="mt-2 text-[11px] text-danger">
            {retractMutation.error instanceof ApiError ? retractMutation.error.message : 'Could not remove that entry'}
          </div>
        )}

        {adding && (
          <div className="mt-3 flex flex-col gap-2.5 rounded-[var(--radius)] border border-border p-3">
            <VoiceFormFiller fields={VOICE_FIELDS} onFields={applyVoiceFields} label="🎙 Fill this diagnosis by voice" />
            <FormField label="Diagnosis" required labelExtra={<VoiceMicButton onText={t => setDiagnosis(v => (v ? v + ' ' : '') + t)} />}>
              <Input value={diagnosis} onChange={e => setDiagnosis(e.target.value)} placeholder="e.g. Type 2 diabetes mellitus" autoFocus />
            </FormField>
            <div className="grid grid-cols-2 gap-2.5">
              <FormField label="Certainty">
                <NativeSelect value={verificationStatus} onChange={e => setVerificationStatus(e.target.value as DiagnosisVerificationStatus)}>
                  {VERIFICATION_OPTIONS.map(v => <option key={v} value={v}>{v}</option>)}
                </NativeSelect>
              </FormField>
              <label className="flex items-center gap-2 self-end pb-2 text-[13px]">
                <Checkbox checked={primary} onCheckedChange={(c) => setPrimary(c === true)} />
                Primary diagnosis for this visit
              </label>
            </div>
            {formError && <div className="text-[11px] text-danger">{formError}</div>}
            <div className="flex gap-2">
              <Button size="sm" loading={createMutation.isPending} onClick={submit}>Save diagnosis</Button>
              <Button size="sm" variant="ghost" onClick={resetForm}>Cancel</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
